import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import {
  ManagementService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets, NgCommonModule } from '@principle-theorem/ng-shared';
import {
  IPracticeSetupStep,
  PracticeSetupStepStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_FORMAT,
  firstValueFrom,
  snapshotDefined,
  toTimestamp,
} from '@principle-theorem/shared';
import {
  AssignStepDialogComponent,
  type IAssignStepDialogData,
  type IAssignStepFormData,
} from '../assign-step-dialog/assign-step-dialog.component';
import { PracticeSetupStepStatusComponent } from '../practice-setup-step-status/practice-setup-step-status.component';
import { SetupStepStore } from '../setup-step.store';
import { PracticeSetupWizardStore } from '../setup-wizard.store';

@Component({
  selector: 'pr-setup-wizard-step',
  templateUrl: './setup-wizard-step.component.html',
  styleUrls: ['./setup-wizard-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SetupStepStore],
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    PracticeSetupStepStatusComponent,
    ContentViewComponent,
  ],
})
export class SetupWizardStepComponent {
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _organisation = inject(OrganisationService);
  private _management = inject(ManagementService);

  wizardStore = inject(PracticeSetupWizardStore);
  stepStore = inject(SetupStepStore);
  step = input.required<IPracticeSetupStep>();
  allSteps = input<IPracticeSetupStep[]>([]);
  readonly dateFormat = CASUAL_DATE_FORMAT;

  unmetDependencies: Signal<IPracticeSetupStep[]>;
  hasUnmetDependencies: Signal<boolean>;
  isManagement: Signal<boolean>;

  constructor() {
    effect(() => {
      this.stepStore.setStep(this.step());
    });

    this.unmetDependencies = computed(() => this._computeUnmetDependencies());
    this.hasUnmetDependencies = computed(
      () => this.unmetDependencies().length > 0
    );
    const managementUser = toSignal(this._management.user$, {
      initialValue: undefined,
    });
    this.isManagement = computed(() => !!managementUser());
  }

  getStepProgressTooltip(): string {
    const progress = this.stepStore.stepProgress();
    if (!progress) {
      return '';
    }
    return `Progress: ${progress.visitedCount}/${progress.requiredCount}`;
  }

  async navigateToStep(): Promise<void> {
    const definition = this.stepStore.definition();
    if (!definition) {
      return;
    }

    const brand = await snapshotDefined(this._organisation.brand$);
    const practice = await snapshotDefined(this._organisation.practice$);
    const route = definition.getRoute(brand, practice);
    await this._router.navigate([route]);
  }

  getMarkCompleteTooltip(): string {
    const step = this.step();

    if (this.stepStore.mutating()) {
      return 'Completing step...';
    }

    if (!step) {
      return '';
    }

    const liveErrors = this.stepStore.liveValidationErrors();
    if (liveErrors?.length) {
      return liveErrors.join('\n');
    }

    if (step.validationErrors?.length) {
      return step.validationErrors.join('\n');
    }

    if (this.hasUnmetDependencies()) {
      return 'Complete prerequisites first';
    }

    if (!this.stepStore.canComplete()) {
      return 'Step requirements not met';
    }

    return 'Mark this step as complete';
  }

  async markComplete(overrideCompleted: boolean = false): Promise<void> {
    const step = this.step();
    const isInvalidOrHasUnmetDeps =
      !this.stepStore.canComplete() || this.hasUnmetDependencies();

    if (!step || (!this.isManagement() && isInvalidOrHasUnmetDeps)) {
      return;
    }

    const staffer = await snapshotDefined(this._organisation.staffer$);
    if (!staffer) {
      return;
    }

    this.wizardStore.completeStep({
      stepId: step.id,
      completedBy: staffer,
      overrideCompleted,
    });
  }

  async openAssignDialog(): Promise<void> {
    const step = this.step();
    if (!step) {
      return;
    }

    const practice = await snapshotDefined(this._organisation.practice$);

    const result = await firstValueFrom(
      this._dialog
        .open<
          AssignStepDialogComponent,
          IAssignStepDialogData,
          IAssignStepFormData
        >(
          AssignStepDialogComponent,
          DialogPresets.medium<IAssignStepDialogData>({
            data: {
              step,
              practice,
            },
          })
        )
        .afterClosed()
    );

    if (!result) {
      return;
    }

    const assignedBy = await snapshotDefined(this._organisation.staffer$);

    this.wizardStore.assignStep({
      stepId: step.id,
      assignee: { ref: result.assignee.ref, name: result.assignee.user.name },
      dueDate: toTimestamp(result.dueDate),
      assignedBy,
    });
  }

  private _computeUnmetDependencies(): IPracticeSetupStep[] {
    const def = this.stepStore.definition();
    const allSteps = this.allSteps();

    if (!def || !def.dependsOn || def.dependsOn.length === 0) {
      return [];
    }

    const unmet: IPracticeSetupStep[] = [];
    for (const depId of def.dependsOn) {
      const depStep = allSteps.find(({ id }) => id === depId);
      if (depStep && depStep.status !== PracticeSetupStepStatus.Complete) {
        unmet.push(depStep);
      }
    }
    return unmet;
  }
}
